// frontend/src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, useMemo, ReactNode, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // <<< ADDED THIS IMPORT
import { fetchCurrentUser, UserWithLeagueContext, UserLeagueSummary, ApiError } from '@/api/auth';
import {
  mixpanelIdentify,
  mixpanelPeopleSet,
  mixpanelReset,
  mixpanelRegister,
  mixpanelUnregister,
  mixpanelPeopleSetOnce,
  mixpanelTrack
} from '@/services/mixpanel';
import * as Sentry from "@sentry/react";
import { submitLineup, LineupSubmissionPayload } from '@/api/dfs'; // <-- ADD THIS LINE
import toast from 'react-hot-toast'; // <-- ADD THIS LINE
import { tokenStorage } from '@/services/platform';
import { consumeIntendedPath, saveIntendedPath } from '@/utils/intendedRedirect';
import { readAuthIntent, CREATE_LEAGUE_RESUME_PATH } from '@/utils/authIntent';
import { clearLeagueCreationPostLoginMarker } from '@/utils/pendingLeagueCreation';
import { pushLogin, pushLogout } from '@/services/pushNotifications';
import { buildDfsContestSectionHref } from '@/utils/dfsContestNavigation';
import {
  resetRejectedSessionDedupe,
  subscribeToRejectedSession,
} from '@/services/authSessionRecovery';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserWithLeagueContext | null;
  token: string | null;
  isLoading: boolean;
  all_my_season_long_leagues: UserLeagueSummary[] | null;
  completeLogin: (newToken: string, opts?: { suppressNavigation?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  refreshUserContext: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  // Under <BrowserRouter> (non-data router) useNavigate() returns a new function
  // on every pathname change. Callbacks below read navigate through this ref so
  // their identities stay stable - otherwise the initial checkAuth effect re-runs
  // on every navigation, flips isLoading, and ProtectedRoute remounts its page
  // (RCA-AUTH-REMOUNT-2026-08-26.md).
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  // Initial token is loaded asynchronously in the checkAuth effect below so the
  // same code path works for both web (localStorage) and native (Capacitor
  // Preferences, which is async). isLoading covers the brief gap on first paint.
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserWithLeagueContext | null>(null);
  const [all_my_season_long_leagues, setAllMySeasonLongLeagues] = useState<UserLeagueSummary[] | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Handles initial auth check loading state
  const [error, setError] = useState<string | null>(null);
  // Request failures can arrive after a logout or a newer login. Ref mirrors
  // let the global 401 listener compare against the token at event time rather
  // than a stale React closure.
  const tokenRef = useRef<string | null>(null);
  const isAuthenticatedRef = useRef(false);
  const reauthInFlightRef = useRef(false);

  const clearAuthData = useCallback(async () => {
    // Clear the rendered session synchronously. Storage can be asynchronous on
    // native, but the UI must stop showing privileged stale content at once.
    tokenRef.current = null;
    isAuthenticatedRef.current = false;
    setToken(null);
    setUser(null);
    setAllMySeasonLongLeagues(null);
    setIsAuthenticated(false);
    mixpanelReset();
    mixpanelUnregister('User ID');
    mixpanelUnregister('Is Superuser');
    Sentry.setUser(null);
    Sentry.getCurrentScope().clear();
    pushLogout(); // detach this device from the user's push subscription
    try {
      await tokenStorage.remove();
    } catch (storageError) {
      console.error("AuthProvider: failed to remove the rejected token from storage.", storageError);
      Sentry.captureException(storageError, {
        tags: { auth_operation: 'remove_stored_token' },
      });
    }
    console.log("AuthProvider: Auth data cleared.");
  }, []);

  const setupUserSession = useCallback(async (currentUser: UserWithLeagueContext, newToken: string) => {
    await tokenStorage.set(newToken);
    tokenRef.current = newToken;
    isAuthenticatedRef.current = true;
    reauthInFlightRef.current = false;
    resetRejectedSessionDedupe();
    setToken(newToken);
    setUser(currentUser);
    setAllMySeasonLongLeagues(currentUser.all_my_season_long_leagues || []);
    setIsAuthenticated(true);
    setError(null);
    console.log("AuthProvider: Session setup/refreshed. Leagues:", currentUser.all_my_season_long_leagues?.length);

    if (currentUser?.id) {
      const userIdStr = String(currentUser.id);
      mixpanelIdentify(userIdStr);
      mixpanelPeopleSetOnce({
        '$created': new Date().toISOString(),
        'Initial Signup Date': new Date().toISOString().split('T')[0],
      });
      mixpanelPeopleSet({
        'User ID': userIdStr,
        'Is Superuser': currentUser.is_superuser,
        '$last_login': new Date().toISOString(),
        'Last Active Date': new Date().toISOString().split('T')[0],
        'Number of Season-Long Leagues': currentUser.all_my_season_long_leagues?.filter(l => !l.is_mock).length || 0,
      });
      mixpanelRegister({
        'User ID': userIdStr,
        'Is Superuser': currentUser.is_superuser,
      });
      Sentry.setUser({
        id: userIdStr,
      });
      Sentry.setTag("user_id", userIdStr);
      Sentry.setTag("is_superuser", String(currentUser.is_superuser));
      // Tie this device's push subscription to the user (OneSignal external
      // id) and request permission. No-op without VITE_ONESIGNAL_APP_ID.
      pushLogin(userIdStr);
    } else {
      Sentry.setUser(null);
      Sentry.getCurrentScope().clear();
      pushLogout();
    }
  }, []); // Removed `navigate` from here as `setupUserSession` doesn't navigate directly

  const requireReauthentication = useCallback(async (requestPath?: string) => {
    if (reauthInFlightRef.current) return;

    reauthInFlightRef.current = true;
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!currentPath.startsWith('/login') && !currentPath.startsWith('/signup')) {
      saveIntendedPath(currentPath);
    }
    console.warn("Authenticated session rejected; requiring sign-in.", { requestPath });
    mixpanelTrack('Session Reauthentication Required', {
      'Request Path': requestPath || 'unknown',
    });

    await clearAuthData();
    navigateRef.current('/login', {
      replace: true,
      state: { authReason: 'session_expired' },
    });
  }, [clearAuthData]);

  useEffect(() => subscribeToRejectedSession(({ token: rejectedToken, requestPath }) => {
    // Ignore a startup probe (checkAuth handles it), a response that finished
    // after explicit logout, or a late response belonging to an older login.
    if (
      !isAuthenticatedRef.current
      || tokenRef.current !== rejectedToken
      || reauthInFlightRef.current
    ) return;

    void requireReauthentication(requestPath);
  }), [requireReauthentication]);

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const storedToken = await tokenStorage.get();
      if (storedToken) {
        try {
          const currentUserData = await fetchCurrentUser(storedToken);
          await setupUserSession(currentUserData, storedToken);
        } catch (err: any) {
          // A 4xx from /auth/me means this client cannot use the stored session,
          // even when an edge layer generated the response. Never render an
          // authenticated shell without a user. Network failures and 5xxs can be
          // transient, so retain the token while the service recovers.
          const status = err instanceof ApiError ? err.status : err?.response?.status;
          const isClientRejection = typeof status === 'number' && status >= 400 && status < 500;
          if (isClientRejection) {
            console.warn(`[AuthContext checkAuth] Session probe rejected (${status}); requiring sign-in.`);
            await requireReauthentication('/auth/me');
          } else {
            console.warn("[AuthContext checkAuth] Could not verify token (transient), keeping session alive.", err);
            tokenRef.current = storedToken;
            isAuthenticatedRef.current = true;
            setToken(storedToken);
            setIsAuthenticated(true);
          }
        }
      } else {
        await clearAuthData(); // No token found
      }
      setIsLoading(false);
    };
    checkAuth();
  }, [clearAuthData, requireReauthentication, setupUserSession]); // Dependencies for initial auth check

  const completeLogin = useCallback(async (newToken: string, opts?: { suppressNavigation?: boolean }) => {
    if (!newToken) {
        setError("Login completion failed: No token provided.");
        return Promise.reject(new Error("No token provided for login."));
    }
    setError(null);
    try {
        const currentUserData = await fetchCurrentUser(newToken);
        await setupUserSession(currentUserData, newToken);

        // In-context auth (AuthSheet, CREATE-LEAGUE-FUNNEL.md §4): the host
        // page stays mounted and owns what happens next, so session setup is
        // all this call does - no intent routing, no postLoginAction, no
        // navigation.
        if (opts?.suppressNavigation) return Promise.resolve();

        // --- URL INTENT (CREATE-LEAGUE-FUNNEL.md §3.3) ---
        // An allowlisted intent on the auth page's URL outranks every
        // storage-based mechanism below: it survives the private-mode /
        // in-app-browser storage loss that stranded the 2026-08-10
        // commissioner signup on /contests. completeLogin is only called
        // from /signup and /login, so window.location still carries the
        // param here (web OAuth is popup-based - the page never navigated).
        const authIntent = readAuthIntent(window.location.search);
        if (authIntent === 'create_league') {
            // The wizard owns resume-vs-recovery from here; this branch never
            // falls through to /home. Clear the league slot-claim marker
            // and discard any stored intended path so neither can re-fire on
            // a later, unrelated login. The DFS marker branch below is
            // deliberately bypassed, not cleared (§3.4: preempt, don't
            // delete - the DFS flow itself stays untouched).
            clearLeagueCreationPostLoginMarker();
            consumeIntendedPath();
            navigateRef.current(CREATE_LEAGUE_RESUME_PATH, { replace: true });
            return Promise.resolve();
        }
        // --- END URL INTENT ---

        // --- NEW: POST-LOGIN ACTION HANDLER ---
        const actionDataStr = sessionStorage.getItem('postLoginAction');
        if (actionDataStr) {
            const actionData = JSON.parse(actionDataStr);
            const lineupDataStr = localStorage.getItem('pendingDfsLineup');

            if (actionData.action === 'submitDfsLineup' && lineupDataStr) {
                const lineupPayload: LineupSubmissionPayload = JSON.parse(lineupDataStr);

                // Clear action marker to prevent duplicate triggers, but keep
                // pendingDfsLineup in localStorage until submission succeeds
                // so the lineup survives a failed API call.
                sessionStorage.removeItem('postLoginAction');

                mixpanelTrack('Signup Funnel Completed - Lineup Submission', {
                    'Contest ID': lineupPayload.contest_id,
                });

                try {
                    const lineupResponse = await submitLineup(lineupPayload, newToken);
                    localStorage.removeItem('pendingDfsLineup');
                    // Flag for contest page to show GAME ON animation
                    sessionStorage.setItem('showGameOnCelebration', 'true');
                    navigateRef.current(buildDfsContestSectionHref(lineupResponse.contest_id, 'leaderboard'), { replace: true });
                } catch (err: any) {
                    // Lineup stays in localStorage — re-set the action marker
                    // so the next login/refresh can retry
                    sessionStorage.setItem('postLoginAction', JSON.stringify({ action: 'submitDfsLineup' }));
                    toast.error(err.message || 'Could not submit lineup.');
                    navigateRef.current(`/dfs/build/${lineupPayload.contest_id}`, { replace: true });
                }

                return; // IMPORTANT: Stop further navigation logic
            }
        }
        // --- END NEW: POST-LOGIN ACTION HANDLER ---

        const intendedPath = consumeIntendedPath();

        if (intendedPath) {
            navigateRef.current(intendedPath, { replace: true });
        } else {
            navigateRef.current('/home', { replace: true });
        }
        return Promise.resolve();
    } catch (err: any) {
        await clearAuthData();
        const errorMessage = (err instanceof ApiError && err.message) ? err.message : (err.message || "Failed to finalize login. Please try again.");
        setError(errorMessage);
        throw new Error(errorMessage);
    }
  }, [clearAuthData, setupUserSession]);

  const logout = useCallback(async () => {
    const currentPath = window.location.pathname;
    await clearAuthData();
    // If logging out from a protected page, redirect to login. Otherwise, stay or go to home.
    // This is a simple example; your GlobalLayout might also influence this.
    if (currentPath !== '/login' && currentPath !== '/signup' && currentPath !== '/') {
        navigateRef.current('/login', { replace: true });
    } else if (currentPath === '/') {
        // If already at home, and home doesn't require auth, do nothing or refresh
        // If home requires auth, redirecting to login is fine.
        navigateRef.current('/login', { replace: true }); // Or just navigate('/') if home is public
    }
    // If on /login or /signup, no need to navigate again after logout.
  }, [clearAuthData]);

  const refreshUserContext = useCallback(async () => {
    const currentToken = await tokenStorage.get();
    if (currentToken) {
      try {
        const currentUserData = await fetchCurrentUser(currentToken);
        await setupUserSession(currentUserData, currentToken);
      } catch (err: any) {
        // /auth/me has no permission-level 4xx: any client rejection means the
        // current session cannot be used. A bearer-backed 401 normally reaches
        // the global listener first; this also covers 403s from an edge layer.
        // Network failures and 5xxs remain transient.
        const status = err instanceof ApiError ? err.status : err?.response?.status;
        const isClientRejection = typeof status === 'number' && status >= 400 && status < 500;
        if (isClientRejection) {
          console.warn(`[AuthContext refreshUserContext] Session probe rejected (${status}); requiring sign-in.`);
          await requireReauthentication('/auth/me');
        } else {
          console.warn("[AuthContext refreshUserContext] Could not refresh user data (transient).", err);
        }
      }
    }
    // If no token, do nothing — user was never logged in
  }, [requireReauthentication, setupUserSession]);

  const value = useMemo(() => ({
    isAuthenticated,
    user,
    token,
    isLoading,
    all_my_season_long_leagues,
    completeLogin,
    logout,
    error,
    refreshUserContext,
  }), [
    isAuthenticated, user, token, isLoading, all_my_season_long_leagues,
    completeLogin, logout, error, refreshUserContext
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
